/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AdminCustomSelect,
  AdminSpinner,
  Box,
  Div,
  ErrorToast,
  H3,
  H5,
  Icon,
  ListSpinner,
  P,
  VerticalSeparator,
  centsToDollars,
  exportTransactionReportAgent,
  fetchAndDownloadCsvFile,
  formatter,
  getAllTransactionAgent,
  getSumTransactionAgent,
  handleError,
  handleSetUrlSearchParams,
  lightTheme,
  transactionsNoData,
} from '@vgrubs/components';
import {
  AgentIncomeStatus,
  AgentIncomeType,
  AgentSumTransactionsResponse,
  AgentTransactionsType,
  IncomeStatus,
  NewTransactionItem,
} from 'components/src/types/api/wallet.types';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { IncomeDetailAgentModal } from './DetailModals/IncomeDetailAgentModal';
import { WithdrawalDetailModal } from './DetailModals/WithdrawalDetailModal';
import { groupTransactionsByDate, useTransactionsDateRange } from './helpers';
import { AgentTransactionRow } from './TransactionAgentComponents';
import {
  TransactionDateGroup,
  TransactionsExportConfirmModal,
  TransactionsPageHeader,
} from './TransactionComponents';
import {
  IconIncome,
  IconWithdrawal,
  showNoDataText,
} from './TransactonsStyled';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';

// ---------------------------------------------------------------------------
// Filter option lists
// ---------------------------------------------------------------------------

// Map the single UI selection to the array the backend expects.
// Selecting "Pending" includes both `active` and `pending` rows.
const expandIncomeStatus = (
  status: AgentIncomeStatus | null
): AgentIncomeStatus[] | undefined => {
  if (!status) return undefined;
  if (status === AgentIncomeStatus.pending) {
    return [AgentIncomeStatus.active, AgentIncomeStatus.pending];
  }
  return [status];
};

// The sum endpoint takes a single status instead of an array and has no
// `active` value - `pending` covers it there, same as in the list above.
const collapseIncomeStatus = (
  status: AgentIncomeStatus | null
): IncomeStatus | undefined => {
  if (!status) return undefined;
  return status === AgentIncomeStatus.available
    ? IncomeStatus.available
    : IncomeStatus.pending;
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const TransactionsAgent = () => {
  const { id: agentId } = useParams() as { id?: string };

  const [loading, setLoading] = useState(false);
  const [loadingSum, setLoadingSum] = useState(false);
  const [transactions, setTransactions] = useState<NewTransactionItem[]>([]);
  const [transactionsByDate, setTransactionsByDate] = useState<
    { date: string; items: NewTransactionItem[] }[]
  >([]);
  const [sum, setSum] = useState<AgentSumTransactionsResponse>({
    income: 0,
    withdrawals: 0,
    lastIncome: 0,
    lastWithdrawal: 0,
    userName: '',
  });

  const [incomeModal, setIncomeModal] = useState<NewTransactionItem | null>(
    null
  );
  const [withdrawalModal, setWithdrawalModal] =
    useState<Parameters<typeof WithdrawalDetailModal>[0]['modalIsOpen']>(null);

  const limit = 50;
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);

  // ---- URL state ----
  const {
    from,
    to,
    page,
    setPage,
    dateInput,
    handleDateChange,
    handleDateApply,
    clearDate,
    hasDateFilter,
    dateLabel,
    searchParams,
    setSearchParams,
  } = useTransactionsDateRange();

  const txType =
    (searchParams.get('type') as AgentTransactionsType | null) ??
    AgentTransactionsType.incomes;
  const incomeStatus = searchParams.get(
    'incomeStatus'
  ) as AgentIncomeStatus | null;
  const incomeType = searchParams.get('incomeType') as AgentIncomeType | null;

  const setTxType = (next: AgentTransactionsType) =>
    handleSetUrlSearchParams('type', next, setSearchParams);
  const setIncomeStatus = (next: string | null) =>
    handleSetUrlSearchParams('incomeStatus', next, setSearchParams);
  const setIncomeType = (next: string | null) =>
    handleSetUrlSearchParams('incomeType', next, setSearchParams);

  // Whatever status we send to the list has to go to the sum call too, or the
  // income box stops matching the rows below it.
  const sumIncomeStatus =
    txType === AgentTransactionsType.incomes
      ? collapseIncomeStatus(incomeStatus)
      : undefined;

  // ---- Data ----
  async function fetchTransactions(
    nextPage: number,
    nextHasMore: boolean,
    isFilter: boolean
  ) {
    if (!agentId) return;
    if (!nextHasMore) return;

    try {
      setLoading(true);
      const isIncomes = txType === AgentTransactionsType.incomes;
      const { data } = await getAllTransactionAgent(agentId, {
        from,
        to,
        page: nextPage,
        limit,
        type: txType,
        // incomeStatus / incomeType only apply to the incomes side; don't
        // send them on withdrawals even if they're still in URL state.
        incomeStatus: isIncomes ? expandIncomeStatus(incomeStatus) : undefined,
        incomeType: isIncomes ? incomeType ?? undefined : undefined,
      });

      const fetched = data.data;

      if (fetched.length === limit) {
        setPage(Number(nextPage) + 1);
        setHasMore(true);
      } else {
        setHasMore(false);
      }

      const combined = isFilter ? fetched : [...transactions, ...fetched];
      setTransactions(combined);
      setTransactionsByDate(groupTransactionsByDate(combined));
    } catch (error) {
      ErrorToast(handleError(error));
    } finally {
      setLoading(false);
    }
  }

  async function fetchSum() {
    if (!agentId) return;
    try {
      setLoadingSum(true);
      const { data } = await getSumTransactionAgent(agentId, {
        from,
        to,
        incomeStatus: sumIncomeStatus,
      });
      setSum({
        income: data.data.income || 0,
        withdrawals: data.data.withdrawals || 0,
        lastIncome: data.data.lastIncome || 0,
        lastWithdrawal: data.data.lastWithdrawal || 0,
        userName: data.data.userName || '',
      });
    } catch (error) {
      ErrorToast(handleError(error));
    } finally {
      setLoadingSum(false);
    }
  }

  async function exportReport() {
    if (!agentId) return;
    const fromISO = DateTime.fromISO(from).toISODate() || '';
    const toISO = DateTime.fromISO(to).toISODate() || '';

    try {
      setLoading(true);
      const { data } = await exportTransactionReportAgent(agentId, {
        from: fromISO,
        to: toISO,
      });

      if (Array.isArray(data.data) && data.data.length > 0) {
        for (const url of data.data) {
          await fetchAndDownloadCsvFile(url);
        }
      } else {
        ErrorToast('No files available for download.');
      }
    } catch (error) {
      ErrorToast(handleError(error));
    } finally {
      setLoading(false);
    }
  }

  // Refetch list whenever filters change. Drop `page` from URL on reset.
  useEffect(() => {
    handleSetUrlSearchParams('page', null, setSearchParams, true);
    setTransactions([]);
    setHasMore(true);
    fetchTransactions(1, true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, txType, incomeStatus, incomeType]);

  // Infinite scroll
  useEffect(() => {
    if (inView && transactions.length > 0) {
      fetchTransactions(page, hasMore, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  // Refetch sum on date or status change (sum is not type-scoped per swagger)
  useEffect(() => {
    fetchSum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, sumIncomeStatus]);

  // Income status/type don't apply to withdrawals — clear them on switch so
  // they don't get sent and so the URL stays clean.
  useEffect(() => {
    if (txType === AgentTransactionsType.withdrawals) {
      if (incomeStatus) setIncomeStatus(null);
      if (incomeType) setIncomeType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txType]);

  const handleRowClick = (item: NewTransactionItem) => {
    if (txType === AgentTransactionsType.incomes) {
      setIncomeModal(item);
    } else {
      // WithdrawalDetailModal expects the legacy wrapper shape; agent
      // withdrawal payload matches per swagger, cast is safe at runtime.
      setWithdrawalModal(
        item as unknown as Parameters<
          typeof WithdrawalDetailModal
        >[0]['modalIsOpen']
      );
    }
  };

  return (
    <div>
      {loading && transactions.length === 0 && <AdminSpinner />}
      <TransactionsPageHeader
        dateInput={dateInput}
        handleDateChange={handleDateChange}
        handleDateApply={handleDateApply}
        clearDate={clearDate}
        hasDateFilter={hasDateFilter}
        dateLabel={dateLabel}
        onExport={() => setExportConfirmOpen(true)}
        incomeStatus={incomeStatus}
        setIncomeStatus={setIncomeStatus}
        incomeType={incomeType}
        setIncomeType={setIncomeType}
        showIncomeFilters={txType === AgentTransactionsType.incomes}
        userName={sum.userName}
      />
      <TransactionsExportConfirmModal
        isOpen={exportConfirmOpen}
        closeModal={() => setExportConfirmOpen(false)}
        onConfirm={() => exportReport()}
        dateLabel={dateLabel}
      />
      <Div $desktop $mb="24">
        <H5 $mb="0">{dateLabel}</H5>
      </Div>

      {/* Sum boxes double as type switcher */}
      <Div
        $flex
        $columnSM
        $gap="20px"
        $gapSM="16px"
        $mb="24"
        $mbSM="16"
        $mtSM="12"
      >
        <Box
          $flex
          $alignCenter
          $pointer
          $width="50%"
          $widthSM="100%"
          $box={txType === AgentTransactionsType.incomes}
          $outlineBox={txType !== AgentTransactionsType.incomes}
          onClick={() => setTxType(AgentTransactionsType.incomes)}
          $relative
          $overflow="hidden"
        >
          {loadingSum && <AdminSpinner absolute small $noZIndex />}
          <Icon>
            <IconIncome />
          </Icon>
          <VerticalSeparator
            $m="0 20px"
            $mSM="0 16px"
            $h="40px"
            $color={lightTheme.lightGray}
          />
          <Div>
            <H5 $m="0">Total Income</H5>
            <H3
              $m="0"
              $color={
                sum.income < 0
                  ? lightTheme.primary
                  : sum.income === 0
                  ? lightTheme.darkGray
                  : lightTheme.green
              }
            >
              {formatter.format(centsToDollars(sum.income))}
            </H3>
            <P $m="0">
              Last Income:{' '}
              {formatter.format(centsToDollars(sum.lastIncome ?? 0))}
            </P>
          </Div>
        </Box>
        <Box
          $p="16px 24px"
          $flex
          $alignCenter
          $pointer
          $width="50%"
          $widthSM="100%"
          $box={txType === AgentTransactionsType.withdrawals}
          $outlineBox={txType !== AgentTransactionsType.withdrawals}
          onClick={() => setTxType(AgentTransactionsType.withdrawals)}
          $relative
          $overflow="hidden"
        >
          {loadingSum && <AdminSpinner absolute small $noZIndex />}
          <Icon>
            <IconWithdrawal />
          </Icon>
          <VerticalSeparator
            $m="0 20px"
            $mSM="0 16px"
            $h="40px"
            $color={lightTheme.lightGray}
          />
          <Div>
            <H5 $m="0">Withdrawals</H5>
            <H3 $m="0">{formatter.format(centsToDollars(sum.withdrawals))}</H3>
            <P $m="0">
              Last Withdrawal:{' '}
              {formatter.format(centsToDollars(sum.lastWithdrawal ?? 0))}
            </P>
          </Div>
        </Box>
      </Div>

      {/* List */}
      {transactionsByDate.map((group, i) => (
        <TransactionDateGroup<NewTransactionItem>
          key={group.date}
          date={group.date}
          items={group.items}
          index={i}
          page={page}
          renderItem={(item, key) => (
            <AgentTransactionRow
              key={key}
              item={item}
              txType={txType}
              onClick={() => handleRowClick(item)}
            />
          )}
        />
      ))}

      {/* Empty state */}
      {transactions.length < 1 && !loading && (
        <NoDataComponent
          image={transactionsNoData}
          title={showNoDataText(
            txType === AgentTransactionsType.incomes ? 'Incomes' : 'Withdrawals'
          )}
          heightSM="calc(100vh - 450px)"
        />
      )}

      <IncomeDetailAgentModal
        modalIsOpen={incomeModal}
        closeModal={() => setIncomeModal(null)}
      />
      <WithdrawalDetailModal
        modalIsOpen={withdrawalModal}
        closeModal={() => setWithdrawalModal(null)}
      />

      <ListSpinner
        loading={loading && transactions.length > 0}
        observerRef={ref}
      />
    </div>
  );
};
